import { Preferences } from '@capacitor/preferences';

// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data',
  TOKEN_EXPIRY: 'token_expiry'
};

// Capacitor-only storage (no fallbacks)
const secureStorage = {
  async getItem(key) {
    try {
      const { value } = await Preferences.get({ key });
      return value;
    } catch (error) {
      console.error('Capacitor storage get error:', error);
      return null;
    }
  },

  async setItem(key, value) {
    try {
      await Preferences.set({ key, value });
      return true;
    } catch (error) {
      console.error('Capacitor storage set error:', error);
      return false;
    }
  },

  async removeItem(key) {
    try {
      await Preferences.remove({ key });
      return true;
    } catch (error) {
      console.error('Capacitor storage remove error:', error);
      return false;
    }
  }
};

// Session management class
class SessionManager {
  constructor() {
    this.accessToken = null;
    this.refreshToken = null;
    this.userData = null;
    this.tokenExpiry = null;
    this.isInitialized = false;
  }

  // Initialize session from storage
  async initialize() {
    if (this.isInitialized) return;

    try {
      const [accessToken, refreshToken, userData, tokenExpiry] = await Promise.all([
        secureStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
        secureStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
        secureStorage.getItem(STORAGE_KEYS.USER_DATA),
        secureStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY)
      ]);

      this.accessToken = accessToken;
      this.refreshToken = refreshToken;
      this.userData = userData ? JSON.parse(userData) : null;
      this.tokenExpiry = tokenExpiry ? parseInt(tokenExpiry) : null;

      this.isInitialized = true;
      console.log('Session initialized:', { 
        hasAccessToken: !!this.accessToken, 
        hasRefreshToken: !!this.refreshToken,
        hasUserData: !!this.userData,
        tokenExpiry: this.tokenExpiry 
      });
    } catch (error) {
      console.error('Failed to initialize session:', error);
      this.isInitialized = true; // Mark as initialized to prevent infinite retries
    }
  }

  // Set session data
  async setSession(accessToken, refreshToken, userData, expiresIn = 3600) {
    try {
      const expiryTime = Date.now() + (expiresIn * 1000);
      
      // Update memory
      this.accessToken = accessToken;
      this.refreshToken = refreshToken;
      this.userData = userData;
      this.tokenExpiry = expiryTime;

      // Update storage
      await Promise.all([
        secureStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken),
        secureStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken),
        secureStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData)),
        secureStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString())
      ]);

      console.log('Session stored successfully');
      return true;
    } catch (error) {
      console.error('Failed to store session:', error);
      return false;
    }
  }

  // Get access token (with expiry check and refresh)
  async getAccessToken() {
    await this.initialize();
    
    if (!this.accessToken) {
      return null;
    }

    // Check if token is expired
    if (this.tokenExpiry && Date.now() >= this.tokenExpiry) {
      const refreshed = await this.refreshAccessToken();
      if (!refreshed) {
        await this.clearSession();
        return null;
      }
    }

    return this.accessToken;
  }

  // Get refresh token
  async getRefreshToken() {
    await this.initialize();
    return this.refreshToken;
  }

  // Get token expiry timestamp
  async getTokenExpiry() {
    await this.initialize();
    return this.tokenExpiry;
  }

  // Refresh access token using refresh token
  async refreshAccessToken() {
    try {
      if (!this.refreshToken) {
        console.log('No refresh token available');
        return false;
      }

      // Call your backend refresh endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://delta-ops.vercel.app'}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: this.refreshToken
        })
      });

      if (!response.ok) {
        console.error('Token refresh failed:', response.status);
        return false;
      }

      const data = await response.json();
      
      if (data.access_token) {
        // Update tokens
        await this.setSession(
          data.access_token,
          data.refresh_token || this.refreshToken,
          this.userData,
          data.expires_in || 3600
        );
        
        console.log('Token refreshed successfully');
        return true;
      }

      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }

  // Get user data
  async getUserData() {
    await this.initialize();
    return this.userData;
  }

  // Check if user is authenticated
  async isAuthenticated() {
    await this.initialize();
    // Allow authentication with just a valid access token (for JWT-only sessions like TeamSignUp)
    // userData can be null during the invite code redemption process
    return !!(this.accessToken);
  }

  // Check if token is expired
  async isTokenExpired() {
    await this.initialize();
    if (!this.tokenExpiry) return true;
    return Date.now() >= this.tokenExpiry;
  }

  // Check if session is valid (not expired and has required data)
  async isSessionValid() {
    await this.initialize();
    
    // For JWT-only sessions (like TeamSignUp), we only need a valid access token
    if (!this.accessToken) {
      return false;
    }

    // Check if token is expired (with 5 minute buffer)
    const bufferTime = 5 * 60 * 1000; // 5 minutes
    if (this.tokenExpiry && Date.now() >= (this.tokenExpiry - bufferTime)) {
      // Try to refresh the token
      const refreshed = await this.refreshAccessToken();
      return refreshed;
    }

    return true;
  }

  // Clear session
  async clearSession() {
    try {
      // Clear memory
      this.accessToken = null;
      this.refreshToken = null;
      this.userData = null;
      this.tokenExpiry = null;

      // Clear storage
      await Promise.all([
        secureStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN),
        secureStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN),
        secureStorage.removeItem(STORAGE_KEYS.USER_DATA),
        secureStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY)
      ]);

      console.log('Session cleared successfully');
      return true;
    } catch (error) {
      console.error('Failed to clear session:', error);
      return false;
    }
  }

  // Get auth headers for API calls
  async getAuthHeaders() {
    const token = await this.getAccessToken();
    if (!token) {
      return {};
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Logout user
  async logout() {
    await this.clearSession();
    // Additional cleanup can be added here
    console.log('User logged out');
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager;
